import React, { useState, useEffect } from 'react';

import { Transforms } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import { determineFileType } from 'utilities/file';

import HelptapEditor from '../../helpers';
import elementTypes from '../../elements/types';
import nodeControls from '../../node-menu/control-types';

import BaseElement from '../base';
import UploadingFileDisplay from './uploading-file-display';
import FileDisplay from './file-display';

import './index.scss';

function HTFile(props) {

  const element = props.element;
  const attributes = element.attributes || element.fileUploadOptions;

  const editor = useSlateStatic();

  const elementPath = ReactEditor.findPath(editor, props.element);

  const [rootState, setRootState] = useState({
    rawFile: attributes.rawFile,
    busy: false,
    working: false
  });

  useEffect(function onLoad() {

    initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onRawFileChange() {

    setRootState({
      ...rootState,
      rawFile: attributes.rawFile
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes.rawFile]);

  async function tryCloneFile() {

    const properties = element.properties || {};
    const cloneFrom = properties.cloneFrom;

    if (typeof cloneFrom !== 'string') {
      return;
    }

    setWorking();

    const pageId = props.parentEntityId;

    const file = await serverInterface.startup.pages.cloneFile(pageId, cloneFrom);

    setIdle();

    HelptapEditor.setAttributes(editor, {
      attributes: {
        ...attributes,
        fileType: determineFileType({ type: file.mimetype }),
        file
      },
      properties: {
        ...properties,
        cloneFrom: null
      }
    }, elementPath);

    HelptapEditor.setNodeIdle(editor, elementPath);

  }

  function initialize() {

    tryCloneFile();

  }

  function handleRawFile(rawFile) {

    setRootState({
      ...rootState,
      rawFile
    });

    HelptapEditor.setAttributes(editor, {
      attributes: {
        ...attributes,
        fileType: determineFileType(rawFile),
        file: null,
        rawFile
      }
    }, ReactEditor.findPath(editor, props.element));

  }

  function handleError(message, deleteNode) {

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message,
      seconds: 5,
      type: 'error'
    });

    if (deleteNode === true) {
      HelptapEditor.deleteNodeByPath(editor, elementPath);
    }

  }

  function onFileUploadingComplete(file) {

    const elementPath = ReactEditor.findPath(editor, props.element);

    HelptapEditor.setAttributes(editor, {
      attributes: {
        ...attributes,
        fileType: determineFileType(rootState.rawFile),
        file
      }
    }, elementPath);

    HelptapEditor.setNodeIdle(editor, elementPath);

  }

  function setWorking() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        working: true
      };

    });

  }

  function setIdle() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        working: false
      };

    });

  }

  function setURLFile(details) {

    HelptapEditor.setAttributes(editor, {
      attributes: {
        ...attributes,
        file: {
          ...details,
          url: details.uri
        },
        showFileDialog: false,
        fileType: details.type
      }
    }, elementPath);

  }

  function switchToParagraphNode(url) {

    HelptapEditor.setAttributes(editor, {
      type: elementTypes.DEFAULT,
      children: [{
        text: url
      }],
      attributes: void 0,
      resizable: void 0
    }, elementPath);

  }

  function switchToURLEmbed(url, details) {

    HelptapEditor.setAttributes(editor, {
      type: elementTypes.URL_EMBED,
      urlEmbedOptions: {
        ...rootState,
        url,
        inputValid: true,
        dataLoaded: true,
        dataAvailable: true,
        data: details
      }
    }, elementPath);

  }

  async function handleURLChange(url) {

    try {

      setWorking();

      const details = await serverInterface.system.getURIDetails({ url });

      setIdle();

      if (details.type === 'UNKNOWN') {
        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: 'Unknown resource!',
          seconds: 5,
          type: 'error'
        });
        switchToParagraphNode(url);
        return;
      }

      if (details.mimetype !== 'text/html') {
        setURLFile(details);
        return;
      }

      const data = details.metadata;

      if (data.error === true) {
        switchToParagraphNode(url);
        return;
      }

      switchToURLEmbed(url, data.result);

    } catch (exception) {

      if (exception.code !== 'ValidationError') {
        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: 'Unknown error! Please contact administrator!!',
          seconds: 5,
          type: 'error'
        });
        return;
      }

      const errors = exception.metadata;

      for (let error of errors) {

        if (error.instancePath === '/url') {

          application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
            message: 'Invalid url! Please provide correct url!!',
            seconds: 5,
            type: 'error'
          });
          return;

        }

      }

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Our programmers have failed to handle a validation error! Kindly let us know!',
        seconds: 5,
        type: 'error'
      });

    } finally {
      setIdle();
    }

  }

  function renderUploadingFileDisplay() {

    if (rootState.rawFile === null) {
      return;
    }

    if (attributes.file !== null) {
      return;
    }

    const uploadingFileDisplayProperties = {
      element,
      attributes,
      fileType: attributes.fileType,
      rawFile: rootState.rawFile,
      uploader: props.mediaUploader,
      onComplete(file) {
        onFileUploadingComplete(file);
      },
      onError(message, deleteNode) {
        handleError(message, deleteNode);
      },
      switchToParagraphNode
    };

    return <UploadingFileDisplay {...uploadingFileDisplayProperties} />;

  }

  function renderFileDisplay() {

    if (attributes.file === null) {
      return;
    }

    const fileDisplayProperties = {
      element,
      attributes,
      handleURLChange,
      handleRawFile,
      editorMainReference: props.editorMainReference
    };

    return <FileDisplay {...fileDisplayProperties} />;

  }

  function tryRenderWorkingIndicator() {

    if (rootState.working === false) {
      return;
    }

    return <div className="working-indicator-container">
      <ht-spinner />
    </div>;

  }

  function renderHTFileContent() {

    const mainAttributes = {
      ...props.attributes,
      contentEditable: false,
      className: 'editor-file-element',
      onClick(event) {
        Transforms.select(editor, elementPath);
      }
    };

    return <div {...mainAttributes}>
      {renderUploadingFileDisplay()}
      {renderFileDisplay()}
      {tryRenderWorkingIndicator()}
      {props.children}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'ht-file-main',
      customNodeControls: [
        nodeControls.URL_INPUT
      ],
      disabledNodeControls: [nodeControls.TEXT_COLOR]
    };

    return <BaseElement {...baseElementProperties}>
      {renderHTFileContent()}
    </BaseElement>;

  }

  return renderMain();

}

export default HTFile;